import { useState, useEffect, useCallback } from 'react';
import { purchasingAPI, GoodsReceiptNote } from '../../../lib/api';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card, CardContent } from '../../ui/card';
import { Loader2, Search, Wrench, AlertTriangle, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import LinkScheduleModal from '../modals/LinkScheduleModal';
import PoLineLink from '../../common/PoLineLink';
import PoHeaderLink from '../../common/PoHeaderLink';

const PAGE_SIZE = 25;

interface UnallocatedGRNsTabProps {
  /** Open the read-only delivery detail modal for a GRN. */
  onViewGrn: (grnId: number) => void;
}

/**
 * Tab listing GRNs the attribution pipeline left unallocated (no schedule FK +
 * zero allocation rows). Visible to superusers and staff (backend gate is
 * ``_require_repair_access``). Each row has a Manual Override
 * button that opens the schedule-line picker to link the GRN.
 */
export default function UnallocatedGRNsTab({ onViewGrn }: UnallocatedGRNsTabProps) {
  const { formatDate } = useFormatters();
  const [grns, setGrns] = useState<GoodsReceiptNote[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkTarget, setLinkTarget] = useState<GoodsReceiptNote | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = { page, page_size: PAGE_SIZE };
      if (search) params['search'] = search;
      const res = await purchasingAPI.getUnallocatedGRNs(params);
      setGrns(res.data.results);
      setCount(res.data.count);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to load unallocated GRNs'));
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const submitSearch = () => { setPage(1); setSearch(searchInput.trim()); };

  return (
    <div className="space-y-3">
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="flex items-start gap-2 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            GRN repair tool. These GRNs were received but never attributed to a
            schedule line. Use <strong>Manual Override</strong> to link a GRN to the
            correct schedule line — the whole GRN qty is allocated to the line you
            pick, and only that line is reconciled.
          </span>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            className="pl-8"
            placeholder="Search GRN #, part, PO number…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitSearch(); }}
          />
        </div>
        <Button variant="outline" onClick={submitSearch}>Search</Button>
        <Button variant="outline" size="icon" onClick={fetchData} title="Refresh">
          <RefreshCw className="h-4 w-4" />
        </Button>
        <span className="ml-auto text-sm text-gray-500">{count} unallocated</span>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center p-8 text-gray-500">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 p-4 text-red-600">
              <AlertTriangle className="h-4 w-4" /> {error}
            </div>
          ) : grns.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No unallocated GRNs. 🎉
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-100 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">GRN #</th>
                  <th className="px-3 py-2">PO / Line</th>
                  <th className="px-3 py-2">Part</th>
                  <th className="px-3 py-2">Supplier</th>
                  <th className="px-3 py-2">Receipt date</th>
                  <th className="px-3 py-2 text-right">Qty received</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {grns.map((grn) => (
                  <tr key={grn.id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <button
                        className="font-medium text-blue-600 hover:underline"
                        onClick={() => onViewGrn(grn.id)}
                      >
                        {grn.grn_number}
                      </button>
                    </td>
                    {/* CR-1154: PO number links to the PO header; line to the line item. */}
                    <td className="px-3 py-2">
                      <PoHeaderLink poLineId={grn.po_line} poNumber={grn.po_number}>{grn.po_number}</PoHeaderLink>
                      {' / '}
                      <PoLineLink poLineId={grn.po_line}>{grn.line_number}</PoLineLink>
                    </td>
                    <td className="px-3 py-2">{grn.part_number}</td>
                    <td className="px-3 py-2">{grn.supplier_name}</td>
                    <td className="px-3 py-2">{formatDate(grn.receipt_date)}</td>
                    <td className="px-3 py-2 text-right">{grn.quantity_received}</td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" onClick={() => setLinkTarget(grn)}>
                        <Wrench className="mr-1.5 h-3.5 w-3.5" /> Manual Override
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button variant="outline" size="icon" disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span>Page {page} of {totalPages}</span>
          <Button variant="outline" size="icon" disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {linkTarget && (
        <LinkScheduleModal
          grn={linkTarget}
          onClose={() => setLinkTarget(null)}
          onLinked={fetchData}
        />
      )}
    </div>
  );
}
